import { useCallback, useState } from 'react';

import { useSelector } from 'react-redux';

import { personGuidSel } from '../duck/selectors/general.selectors';

type Preference = boolean | number | string;
type Widen<T> = T extends boolean ? boolean : T extends number ? number : T;
type StoredPreferences = Record<string, Record<string, unknown> | undefined>;

// Same store and shape as `userPreferencesServices`
const STORAGE_KEY = 'USER_PREFERENCES';

function readStore(): StoredPreferences {
  const data: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  return data && typeof data === 'object' ? (data as StoredPreferences) : {};
}

function readPreference<T extends Preference>(
  personGuid: string | undefined,
  featureName: string,
  defaultValue: T,
): T {
  if (!personGuid) return defaultValue;
  try {
    const value = readStore()[personGuid]?.[featureName];
    return typeof value === typeof defaultValue ? (value as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function writePreference(
  personGuid: string,
  featureName: string,
  value: Preference,
): void {
  try {
    const store = readStore();
    store[personGuid] = { ...store[personGuid], [featureName]: value };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // Storage full or blocked: the value still works, it's just not kept
  }
}

/**
 * The logged-in user's `featureName` preference in `USER_PREFERENCES`, re-read
 * when the user changes. Other types read as `defaultValue`; no user, no saving.
 */
export function useUserPreference<T extends Preference>(
  featureName: string,
  defaultValue: T,
): [Widen<T>, (value: Widen<T>) => void] {
  const personGuid: string | undefined =
    useSelector(personGuidSel) || undefined;
  const [state, setState] = useState(() => ({
    personGuid,
    value: readPreference(personGuid, featureName, defaultValue),
  }));

  let { value } = state;
  if (state.personGuid !== personGuid) {
    // Updating during render: React re-renders before committing
    value = readPreference(personGuid, featureName, defaultValue);
    setState({ personGuid, value });
  }

  const setValue = useCallback(
    (next: Widen<T>) => {
      setState({ personGuid, value: next as T });
      if (personGuid) writePreference(personGuid, featureName, next);
    },
    [personGuid, featureName],
  );

  return [value as Widen<T>, setValue];
}
